// src/Game.js
//
// Kusa: 4-player partnership trick-taking card game with bidding.
// Partners sit opposite (0+2 vs 1+3). First team to targetScore wins.

import { INVALID_MOVE } from 'boardgame.io/core';

// ============================================================
// CARDS
// ============================================================
const COLORS = ['red', 'yellow', 'green', 'blue'];
const RANKS = [1, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

const COUNTER_VALUE = { 1: 15, 5: 5, 10: 10, 14: 10 };
const KUSA_COUNTER = 20;
const TOTAL_COUNTERS = 180;

const RANK_PRIORITY = [1, 14, 13, 12, 11, 10, 9, 8, 7, 6, 5];
const rankIdx = (rank) => RANK_PRIORITY.indexOf(rank);

const isKusa = (card) => Boolean(card && card.kusa);
const cardKey = (card) =>
  isKusa(card) ? 'KUSA' : `${card.color}-${card.rank}`;
const counterValueOf = (card) =>
  isKusa(card) ? KUSA_COUNTER : (COUNTER_VALUE[card.rank] || 0);

function buildDeck() {
  const deck = [];
  for (const color of COLORS) {
    for (const rank of RANKS) deck.push({ color, rank });
  }
  deck.push({ kusa: true });
  return deck;
}

const teamOf = (playerID) => Number(playerID) % 2;
const partnerOf = (playerID) => String((Number(playerID) + 2) % 4);

// ============================================================
// CONFIG
// ============================================================
export const TARGET_SCORE_OPTIONS = [300, 500, 750, 1000, 1500];
const DEFAULT_TARGET_SCORE = 300;

export const VALID_BIDS = [
  70, 75, 80, 85, 90, 95, 100,
  105, 110, 115, 120, 125, 130, 135, 140,
  145, 150, 155, 160, 165, 170, 175, 180,
];

// The bid amount IS the contract — kept as a function so callers don't
// need to change if we ever reintroduce a bid→contract mapping.
export function bidToContract(bid) {
  return bid;
}

// ============================================================
// TRICK LOGIC
// ============================================================
function isTrump(card, trump) {
  if (trump === 'no-trump') return false;
  if (isKusa(card)) return true;
  return card.color === trump;
}

function isLegalPlay(card, hand, leadColor, trump) {
  if (leadColor === null) return true;
  // Kusa is a free trump — never has to follow suit.
  if (isKusa(card)) return true;

  if (leadColor === 'KUSA') {
    if (trump === 'no-trump') return true;
    const hasTrump = hand.some((c) => isTrump(c, trump));
    return hasTrump ? isTrump(card, trump) : true;
  }

  if (leadColor === trump) {
    const hasTrump = hand.some((c) => isTrump(c, trump));
    return hasTrump ? isTrump(card, trump) : true;
  }

  const hasLead = hand.some((c) => !isKusa(c) && c.color === leadColor);
  if (hasLead) return !isKusa(card) && card.color === leadColor;
  return true;
}

// Public helpers for the UI.
export function legalPlayKeys(G, playerID) {
  const hand = G.hands[playerID] || [];
  if (G.currentTrick.length === 0) {
    return new Set(hand.filter((c) => !c.hidden).map(cardKey));
  }
  return new Set(
    hand
      .filter((c) => !c.hidden && isLegalPlay(c, hand, G.leadColor, G.trump))
      .map(cardKey)
  );
}

// Returns the playerID of the player currently winning the trick by
// "trumping in" — i.e. by playing a trump card when the trick was led
// with a non-trump color. Returns null otherwise (no cards played, no
// trump declared, trump was led, or no trump played in the trick).
//
// The UI uses this to highlight the seat that's currently winning the
// trick on the strength of a trump play, so players can see at a glance
// when someone has "burned in" with trump.
export function trumpingPlayer(trick, trump) {
  if (!trick || trick.length === 0) return null;
  if (!trump || trump === 'no-trump') return null;
  const firstCard = trick[0].card;
  const leadColor = isKusa(firstCard) ? 'KUSA' : firstCard.color;
  // Trump was led — everyone is following suit with trumps, no one is
  // "trumping in."
  if (leadColor === trump || leadColor === 'KUSA') return null;
  const winner = trickWinner(trick, leadColor, trump);
  if (!isTrump(winner.card, trump)) return null;
  return winner.player;
}

export function whyIllegal(G, playerID, card) {
  if (!card || card.hidden) return null;
  if (G.currentTrick.length === 0) return null;
  if (isKusa(card)) return null;
  const hand = G.hands[playerID] || [];
  const leadColor = G.leadColor;
  const trump = G.trump;

  if (leadColor === 'KUSA' || leadColor === trump) {
    const hasTrump = hand.some((c) => isTrump(c, trump));
    if (hasTrump && !isTrump(card, trump)) {
      return 'Must play a trump';
    }
    return null;
  }

  const hasLead = hand.some((c) => !isKusa(c) && c.color === leadColor);
  if (hasLead && (isKusa(card) || card.color !== leadColor)) {
    return `Must follow ${leadColor}`;
  }
  return null;
}

function winningCard(a, b, leadColor, trump) {
  const aT = isTrump(a, trump);
  const bT = isTrump(b, trump);
  if (aT && !bT) return a;
  if (bT && !aT) return b;
  if (aT && bT) {
    if (isKusa(a)) return a;
    if (isKusa(b)) return b;
    return rankIdx(a.rank) < rankIdx(b.rank) ? a : b;
  }
  const aLead = !isKusa(a) && a.color === leadColor;
  const bLead = !isKusa(b) && b.color === leadColor;
  if (aLead && !bLead) return a;
  if (bLead && !aLead) return b;
  if (aLead && bLead) {
    return rankIdx(a.rank) < rankIdx(b.rank) ? a : b;
  }
  return a;
}

function trickWinner(trick, leadColor, trump) {
  let win = trick[0];
  for (let i = 1; i < trick.length; i++) {
    if (
      winningCard(win.card, trick[i].card, leadColor, trump) === trick[i].card
    ) {
      win = trick[i];
    }
  }
  return win;
}

const tally = (cards) =>
  (cards || []).reduce((sum, c) => sum + counterValueOf(c), 0);

// ============================================================
// SETUP / DEAL
// ============================================================
function dealHand(G, random) {
  const shuffled = random.Shuffle(buildDeck());
  G.hands = { 0: [], 1: [], 2: [], 3: [] };
  for (let p = 0; p < 4; p++) G.hands[p] = shuffled.splice(0, 10);
  G.nest = shuffled.splice(0, 5);
  G.nestFaceUp = G.nest[G.nest.length - 1];

  G.blindBidding = !!(G.nestFaceUp && G.nestFaceUp.kusa);

  G.highBid = null;
  G.passes = [];
  G.bidHistory = [];
  G.declarer = null;
  G.contractAmount = null;
  G.contractDisplay = null;
  G.trump = null;
  G.trumpCaller = null;
  G.partnerCalledTrump = false;
  G.buried = null;
  G.currentTrick = [];
  G.lastTrick = null;
  G.lastTrickWinner = null;
  G.leadColor = null;
  G.tricksWon = { 0: [], 1: [] };
  G.trickCount = 0;
  G.leader = null;
}

// Setup signature is flexible — boardgame.io passes either
// (setupData) or ({ ctx, random, ... }, setupData) depending on version.
const setup = (a, b) => {
  // Whichever arg has targetScore wins.
  const data = (b && b.targetScore !== undefined) ? b
              : (a && a.targetScore !== undefined) ? a
              : null;
  const target = data && data.targetScore;
  return {
    dealer: 0,
    hands: { 0: [], 1: [], 2: [], 3: [] },
    nest: [],
    nestFaceUp: null,
    blindBidding: false,
    highBid: null,
    passes: [],
    bidHistory: [],
    declarer: null,
    contractAmount: null,
    contractDisplay: null,
    trump: null,
    trumpCaller: null,
    partnerCalledTrump: false,
    buried: null,
    currentTrick: [],
    lastTrick: null,
    lastTrickWinner: null,
    leadColor: null,
    tricksWon: { 0: [], 1: [] },
    trickCount: 0,
    leader: null,
    score: { 0: 0, 1: 0 },
    handHistory: [],
    targetScore: TARGET_SCORE_OPTIONS.includes(target)
      ? target
      : DEFAULT_TARGET_SCORE,
  };
};

// ============================================================
// PHASE 1: BIDDING
// ============================================================
const bidding = {
  start: true,

  onBegin: ({ G, random }) => {
    // Deal a fresh hand at the start of bidding if we don't have one
    // (i.e. the previous hand has been resolved and cleared). The game-
    // over branch is handled by play.next routing to 'complete' rather
    // than here.
    if (G.hands[0].length === 0) {
      dealHand(G, random);
    }
  },

  turn: {
    order: {
      first: ({ G }) => (G.dealer + 1) % 4,
      next: ({ G, ctx }) => {
        let defaultNext;
        for (let i = 1; i <= 4; i++) {
          const candidate = (ctx.playOrderPos + i) % 4;
          if (!G.passes.includes(String(candidate))) {
            defaultNext = candidate;
            break;
          }
        }

        if (G.highBid) {
          const bidder = G.highBid.player;
          const bidderTeam = teamOf(bidder);
          const opponents = ['0', '1', '2', '3'].filter(
            (p) => teamOf(p) !== bidderTeam
          );
          const allOppPassed = opponents.every((p) => G.passes.includes(p));
          if (allOppPassed) {
            const partner = partnerOf(bidder);
            if (!G.passes.includes(partner) && partner !== bidder) {
              return Number(partner);
            }
          }
        }

        return defaultNext;
      },
    },
  },

  moves: {
    bid: ({ G, playerID, events }, amount) => {
      if (G.passes.includes(playerID)) return INVALID_MOVE;
      if (!VALID_BIDS.includes(amount)) return INVALID_MOVE;
      if (G.highBid && amount <= G.highBid.amount) return INVALID_MOVE;
      if (G.highBid && G.highBid.player === playerID) return INVALID_MOVE;
      G.highBid = { player: playerID, amount };
      G.bidHistory.push({ player: playerID, action: 'bid', amount });
      events.endTurn();
    },
    pass: ({ G, playerID, events }) => {
      if (G.passes.includes(playerID)) return INVALID_MOVE;
      G.passes.push(playerID);
      G.bidHistory.push({ player: playerID, action: 'pass' });
      events.endTurn();
    },
    hold: ({ G, playerID, events }) => {
      if (G.passes.includes(playerID)) return INVALID_MOVE;
      if (!G.highBid) return INVALID_MOVE;
      if (G.highBid.player === playerID) return INVALID_MOVE;
      if (teamOf(G.highBid.player) !== teamOf(playerID)) return INVALID_MOVE;
      const myTeam = teamOf(playerID);
      const activeOpp = ['0', '1', '2', '3'].filter(
        (p) => !G.passes.includes(p) && teamOf(p) !== myTeam
      );
      if (activeOpp.length === 0) return INVALID_MOVE;
      G.bidHistory.push({ player: playerID, action: 'hold' });
      events.endTurn();
    },
  },

  endIf: ({ G }) => {
    if (G.highBid && G.highBid.amount === 180) return true;
    const active = ['0', '1', '2', '3'].filter(
      (p) => !G.passes.includes(p)
    );
    if (G.highBid && active.length === 1 && active[0] === G.highBid.player) {
      return true;
    }
    if (G.passes.length >= 4) return true;
    return false;
  },

  onEnd: ({ G }) => {
    if (G.highBid) {
      G.declarer = G.highBid.player;
      G.contractDisplay = G.highBid.amount;
      G.contractAmount = bidToContract(G.highBid.amount);
    } else {
      // All four passed — the first passer is stuck with the minimum
      // contract (70, the lowest legal bid).
      G.declarer = G.passes[0];
      G.contractDisplay = 70;
      G.contractAmount = 70;
    }
    G.hands[G.declarer] = G.hands[G.declarer].concat(G.nest);
    G.nest = [];
    G.nestFaceUp = null;
    G.blindBidding = false;
  },

  next: 'discardAndTrump',
};

// ============================================================
// PHASE 2: DISCARD AND TRUMP
// ============================================================
const discardAndTrump = {
  turn: {
    order: {
      first: ({ G }) => Number(G.declarer),
      next: () => undefined,
    },
  },

  moves: {
    discard: ({ G, playerID, events }, cardKeys) => {
      if (playerID !== G.declarer) return INVALID_MOVE;
      if (G.buried !== null) return INVALID_MOVE;
      if (!Array.isArray(cardKeys) || cardKeys.length !== 5) return INVALID_MOVE;
      const hand = G.hands[playerID];
      const wanted = new Set(cardKeys);
      const buried = [];
      const remaining = [];
      for (const c of hand) {
        if (wanted.has(cardKey(c))) buried.push(c);
        else remaining.push(c);
      }
      if (buried.length !== 5) return INVALID_MOVE;
      G.hands[playerID] = remaining;
      G.buried = buried;
      if (G.trump !== null) {
        events.endPhase();
      }
    },

    askPartnerToCallTrump: ({ G, playerID, events }) => {
      if (playerID !== G.declarer) return INVALID_MOVE;
      if (G.trump !== null) return INVALID_MOVE;
      if (G.partnerCalledTrump) return INVALID_MOVE;
      G.partnerCalledTrump = true;
      // Cap contract at 180 — the maximum possible bid. Asking the partner
      // when bid is already 180 effectively becomes a "free" trump-call.
      G.contractAmount = Math.min(180, G.contractAmount + 5);
      events.endTurn({ next: partnerOf(playerID) });
    },

    declareTrump: ({ G, playerID, events }, trump) => {
      const allowed = G.partnerCalledTrump
        ? partnerOf(G.declarer)
        : G.declarer;
      if (playerID !== allowed) return INVALID_MOVE;
      if (G.trump !== null) return INVALID_MOVE;
      if (playerID === G.declarer && G.buried === null) return INVALID_MOVE;
      const valid = ['red', 'yellow', 'green', 'blue', 'no-trump'];
      if (!valid.includes(trump)) return INVALID_MOVE;
      G.trump = trump;
      G.trumpCaller = playerID;

      if (playerID !== G.declarer) {
        if (G.buried === null) {
          events.endTurn({ next: G.declarer });
        } else {
          events.endPhase();
        }
      } else {
        events.endPhase();
      }
    },
  },

  next: 'play',
};

// ============================================================
// PHASE 3: PLAY (10 tricks)
// ============================================================
const play = {
  onBegin: ({ G }) => {
    G.leader = G.trumpCaller;
    G.currentTrick = [];
    G.lastTrick = null;
    G.lastTrickWinner = null;
    G.leadColor = null;
    G.trickCount = 0;
  },

  turn: {
    order: {
      first: ({ G }) => Number(G.trumpCaller),
      next: ({ G, ctx }) => {
        if (G.currentTrick.length === 0 && G.leader !== null) {
          return Number(G.leader);
        }
        return (ctx.playOrderPos + 1) % 4;
      },
    },
  },

  moves: {
    playCard: ({ G, playerID, events }, key) => {
      const hand = G.hands[playerID];
      const idx = hand.findIndex((c) => cardKey(c) === key);
      if (idx === -1) return INVALID_MOVE;
      const card = hand[idx];

      const leadColor = G.currentTrick.length === 0 ? null : G.leadColor;
      if (!isLegalPlay(card, hand, leadColor, G.trump)) return INVALID_MOVE;

      hand.splice(idx, 1);
      G.currentTrick.push({ player: playerID, card });

      if (G.currentTrick.length === 1) {
        G.leadColor = isKusa(card) ? 'KUSA' : card.color;
      }

      if (G.currentTrick.length === 4) {
        const winner = trickWinner(G.currentTrick, G.leadColor, G.trump);
        const team = teamOf(winner.player);
        G.tricksWon[team] = G.tricksWon[team].concat(
          G.currentTrick.map((p) => p.card)
        );
        G.lastTrick = G.currentTrick.map((p) => ({
          player: p.player,
          card: { ...p.card },
        }));
        G.lastTrickWinner = winner.player;
        G.leader = winner.player;
        G.trickCount += 1;
        G.currentTrick = [];
        G.leadColor = null;

        if (G.trickCount >= 10) {
          G.tricksWon[team] = G.tricksWon[team].concat(G.buried || []);
          events.endPhase();
          return;
        }
      }

      events.endTurn();
    },
  },

  onEnd: ({ G, random }) => {
    const counters = {
      0: tally(G.tricksWon[0]),
      1: tally(G.tricksWon[1]),
    };
    const declarerTeam = teamOf(G.declarer);
    const oppTeam = 1 - declarerTeam;

    let declarerDelta;
    if (counters[declarerTeam] >= G.contractAmount) {
      declarerDelta = counters[declarerTeam];
    } else {
      declarerDelta = -G.contractAmount;
    }
    G.score[declarerTeam] += declarerDelta;
    G.score[oppTeam] += counters[oppTeam];

    const hungTheMoon =
      counters[0] === TOTAL_COUNTERS
        ? 0
        : counters[1] === TOTAL_COUNTERS
        ? 1
        : null;

    G.handHistory.push({
      handNumber: G.handHistory.length + 1,
      declarer: G.declarer,
      declarerTeam,
      contract: G.contractAmount,
      contractDisplay: G.contractDisplay,
      trump: G.trump,
      trumpCaller: G.trumpCaller,
      partnerCalledTrump: G.partnerCalledTrump,
      counters,
      made: counters[declarerTeam] >= G.contractAmount,
      delta: { [declarerTeam]: declarerDelta, [oppTeam]: counters[oppTeam] },
      scoreAfter: { ...G.score },
      hungTheMoon,
    });

    G.dealer = (G.dealer + 1) % 4;

    // Don't deal a new hand if the game is over. play.next (below) is
    // a function that will route to the 'complete' phase in that case;
    // we just leave the hands empty so the new bidding phase doesn't
    // begin with a stale hand on screen.
    const target = G.targetScore || DEFAULT_TARGET_SCORE;
    if (G.score[0] >= target || G.score[1] >= target) {
      return;
    }

    if (random) {
      dealHand(G, random);
    }
  },

  // Dynamic next: route to 'complete' if the target score has been hit,
  // otherwise loop back to 'bidding' for the next hand. boardgame.io
  // supports `next` as either a string or a function — using a function
  // here is the supported way to choose a phase based on game state.
  // (events.setPhase from inside bidding.onBegin was fragile because it
  // queues a transition while another transition is already in flight.)
  next: ({ G }) => {
    const target = G.targetScore || DEFAULT_TARGET_SCORE;
    if (G.score[0] >= target || G.score[1] >= target) return 'complete';
    return 'bidding';
  },
};

// ============================================================
// PHASE 4: COMPLETE (game-over hold state)
//
// Entered when the play phase ends and play.next sees that a team has
// reached the target score. Only the would-be next dealer is "on turn"
// here, and the only legal move is playAgain — which resets the score
// and history and bounces back to the bidding phase for a brand-new
// game with the same four players in the same seats.
// ============================================================
const complete = {
  turn: {
    order: {
      first: ({ G }) => Number(G.dealer),
      next: () => undefined,
    },
  },

  moves: {
    playAgain: ({ G, events }) => {
      G.score = { 0: 0, 1: 0 };
      G.handHistory = [];
      // G.dealer was already rotated in play.onEnd when the last hand
      // finished, so we leave it as-is — the natural deal rotation
      // continues into the new game.
      events.setPhase('bidding');
    },
  },
};

// ============================================================
// GAME OBJECT
// ============================================================
export const Kusa = {
  name: 'kusa',
  minPlayers: 4,
  maxPlayers: 4,

  setup,

  phases: { bidding, discardAndTrump, play, complete },

  // No top-level endIf — game-end is modeled as the 'complete' phase
  // rather than a terminal gameover, so playAgain remains callable.

  playerView: ({ G, ctx, playerID }) => {
    if (playerID === null || playerID === undefined) return G;
    const masked = { ...G, hands: { ...G.hands } };
    for (const p of ['0', '1', '2', '3']) {
      if (p !== playerID) {
        masked.hands[p] = G.hands[p].map(() => ({ hidden: true }));
      }
    }
    if (G.blindBidding && ctx.phase === 'bidding') {
      masked.hands[playerID] = G.hands[playerID].map(() => ({ hidden: true }));
    }
    if (G.nest && G.nest.length > 0) {
      masked.nest = G.nest.map((card, i) =>
        i === G.nest.length - 1 ? card : { hidden: true }
      );
    }
    if (G.buried) masked.buried = G.buried.map(() => ({ hidden: true }));
    return masked;
  },
};
